import styled, { css, keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * Visual port of the Remix reference's `routes/campaigns.$campaignId.tsx`.
 * Colours come from `--hd-*` tokens so both themes match the campaigns list.
 */

const CARD_SHADOW = 'var(--hd-shadow-card)';
const GRADIENT = 'var(--hd-gradient-houdiny)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const livePulse = keyframes`
  50% { opacity: 0.5; }
`;

export const StyledCampaignDetail = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: var(--hd-background);
  color: var(--hd-foreground);
  font-family: var(--base-font-sans-serif);
  -webkit-font-smoothing: antialiased;

  .detail-main {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 32px 16px;
    }
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--hd-muted-foreground);
    text-decoration: none;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;

    &:hover {
      color: var(--hd-foreground);
    }
  }

  /* Radii follow version3's scale, where --radius is 14px: rounded-md 12px,
     rounded-xl 18px, rounded-2xl and surface-card 22px. */
  .surface-card {
    background: var(--hd-card);
    border: 1px solid var(--hd-border);
    border-radius: 22px;
    box-shadow: ${CARD_SHADOW};
  }

  .hero-card {
    position: relative;
    overflow: hidden;
    margin-top: 24px;
    padding: 32px;
  }

  .hero-glow {
    pointer-events: none;
    position: absolute;
    top: -96px;
    right: -96px;
    width: 288px;
    height: 288px;
    border-radius: 50%;
    ${({ $tint }) => css`
      background: ${$tint};
    `}
  }

  .hero-row {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;

    @media (min-width: 1024px) {
      flex-direction: row;
      align-items: center;
    }
  }

  .hero-identity {
    display: flex;
    align-items: center;
    gap: 20px;
    min-width: 0;
  }

  .hero-avatar-wrap {
    position: relative;
    flex-shrink: 0;
  }

  .hero-avatar {
    display: block;
    width: 80px;
    height: 80px;
    border-radius: 22px;
    object-fit: cover;
    ${({ $ring }) => css`
      box-shadow: 0 0 0 4px ${$ring};
    `}
  }

  .hero-status-dot {
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    box-shadow: 0 0 0 2px var(--hd-card);
    ${({ $dot }) => css`
      background: ${$dot};
    `}
  }

  .hero-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .hero-persona {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.025em;
    text-transform: none;
    color: var(--hd-foreground);
  }

  .panel-title,
  h1,
  h2,
  h3 {
    text-transform: none;
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    ${({ $tint, $accent }) => css`
      color: ${$accent};
      background: ${$tint};
    `}
  }

  .hero-campaign-name {
    margin: 4px 0 0;
    font-size: 14px;
    color: var(--hd-muted-foreground);
  }

  .hero-description {
    max-width: 36rem;
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.625;
    color: var(--hd-muted-foreground);
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .ghost-btn,
  .primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 12px;
    border-radius: 18px;
    font-size: 14px;
    cursor: pointer;
    box-shadow: ${CARD_SHADOW};

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .ghost-btn {
    border: 1px solid var(--hd-border);
    font-weight: 500;
    color: var(--hd-foreground);
    background: var(--hd-card);

    &:hover:not(:disabled) {
      background: var(--hd-muted);
    }
  }

  .primary-btn {
    border: none;
    font-weight: 600;
    color: #fff;
    background: ${GRADIENT};
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 32px;

    @media (min-width: 768px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .kpi-card {
    padding: 20px;
    animation: ${fadeUp} 0.5s ease-out both;
  }

  .kpi-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--hd-muted-foreground);
  }

  .kpi-value {
    margin-top: 8px;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: var(--hd-foreground);
  }

  .charts-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 32px;

    @media (min-width: 1024px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .chart-wide {
    @media (min-width: 1024px) {
      grid-column: span 2;
    }
  }

  .activity-panel {
    display: flex;
    flex-direction: column;
  }

  /* An icon tile beside a panel title (Campaign health). */
  .panel-identity {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .panel-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 18px;
  }

  .panel-title.with-pill {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .soon-pill {
    padding: 2px 8px;
    border: 1px solid var(--hd-border);
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: normal;
    color: var(--hd-muted-foreground);
    background: var(--hd-card);
  }

  .live-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    color: #059669;
    background: #ecfdf5;
  }

  .live-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    animation: ${livePulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  /* ---- Campaign health ---- */

  .health-card {
    margin-top: 32px;
  }

  .health-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .verdict-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .health-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 24px;

    @media (min-width: 1024px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .health-score {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .score-ring {
    position: relative;
    flex-shrink: 0;
    width: 112px;
    height: 112px;

    svg {
      width: 112px;
      height: 112px;
      transform: rotate(-90deg);
    }

    circle {
      fill: none;
      stroke-width: 3;
    }
  }

  .score-track {
    stroke: var(--hd-muted);
  }

  .score-arc {
    stroke-linecap: round;
  }

  .score-label {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
  }

  .score-value {
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: var(--hd-foreground);
  }

  .score-caption {
    font-size: 10px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--hd-muted-foreground);
  }

  .health-bars {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .health-bar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
  }

  .health-bar-label {
    color: var(--hd-muted-foreground);
  }

  .health-bar-value {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--hd-foreground);
  }

  .health-track {
    margin-top: 4px;
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--hd-muted);
  }

  .health-fill {
    height: 100%;
    border-radius: 999px;
  }

  .health-hint {
    margin: 4px 0 0;
    font-size: 11px;
    line-height: 1.375;
    color: var(--hd-muted-foreground);
  }

  .health-box {
    padding: 16px;
    border: 1px solid var(--hd-border);
    border-radius: 22px;
  }

  .health-box-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--hd-foreground);
  }

  .icon-fixes {
    color: #2563eb;
  }

  .icon-learnings {
    color: #7c3aed;
  }

  .icon-learning {
    flex-shrink: 0;
    margin-top: 2px;
    color: #8b5cf6;
  }

  .health-list {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .health-item {
    padding: 12px;
    border: 1px solid var(--hd-border);
    border-radius: 18px;
    background: var(--hd-card);
    box-shadow: ${CARD_SHADOW};
  }

  .learning-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 12px;
    line-height: 1.375;
    color: var(--hd-foreground);
  }

  .fix-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .fix-title {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.375;
    color: var(--hd-foreground);
  }

  .fix-effort {
    flex-shrink: 0;
    padding: 2px 6px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    color: var(--hd-muted-foreground);
    background: var(--hd-muted);
  }

  .fix-apply {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: #2563eb;
  }

  .learnings-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 36px;
    margin-top: 12px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: ${GRADIENT};
    box-shadow: ${CARD_SHADOW};
  }

  /* ---- Estimated vs reality ---- */

  .estimate-card {
    margin-top: 32px;
  }

  .estimate-legend {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 11px;
    font-weight: 500;
    color: var(--hd-muted-foreground);

    > span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  }

  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .legend-est {
    background: #cbd5e1;
  }

  .legend-real {
    background: ${GRADIENT};
  }

  .estimate-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 20px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .estimate-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 14px;
  }

  .estimate-label {
    color: var(--hd-muted-foreground);
  }

  .delta-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .delta-up {
    color: #059669;
    background: #ecfdf5;
  }

  .delta-down {
    color: #e11d48;
    background: #fff1f2;
  }

  .estimate-bars {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .estimate-bar-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .estimate-value {
    flex-shrink: 0;
    width: 40px;
    text-align: right;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: var(--hd-muted-foreground);
  }

  .estimate-track {
    flex: 1;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--hd-muted);
  }

  .estimate-fill {
    height: 100%;
    border-radius: 999px;
  }

  .panel {
    padding: 24px;
  }

  .panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: var(--hd-foreground);
  }

  .panel-sub {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .trend-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    color: #047857;
    background: #ecfdf5;
  }

  .chart-body {
    margin-top: 16px;
    height: 288px;
  }

  .activity-list {
    flex: 1;
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .activity-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .activity-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
  }

  .activity-text {
    font-size: 13px;
    line-height: 1.375;
    color: var(--hd-foreground);
  }

  .activity-time {
    font-size: 11px;
    color: var(--hd-muted-foreground);
  }

  .empty-note {
    margin-top: 16px;
    font-size: 14px;
    color: var(--hd-muted-foreground);
  }
`;
